import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Flex, Heading, HStack, IconButton, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Table, Tbody, Td, Th, Thead, Tr, useDisclosure, useToast } from "@chakra-ui/react";
import { AddIcon, DeleteIcon, EditIcon, RepeatIcon } from "@chakra-ui/icons";
import rbacService from "../services/rbacService";
import type { Application } from "../../rbac/types";

const ApplicationsPage: React.FC = () => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<Application[]>([]);
  const [search, setSearch] = useState("");

  // form state
  const [editing, setEditing] = useState<Application | null>(null);
  const [appName, setAppName] = useState("");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(a => a.app_name.toLowerCase().includes(q));
  }, [items, search]);

  const load = async () => {
    setLoading(true);
    try {
      const data = await rbacService.getApplications({ skip: 0, limit: 100 });
      setItems(data);
    } catch (e: any) {
      toast({ status: "error", title: "Gagal memuat aplikasi", description: e?.response?.data?.detail || e?.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setAppName("");
    onOpen();
  };

  const openEdit = (row: Application) => {
    setEditing(row);
    setAppName(row.app_name);
    onOpen();
  };

  const submit = async () => {
    try {
      if (editing) {
        await rbacService.updateApplication(editing.id, { app_name: appName });
        toast({ status: "success", title: "Aplikasi diperbarui" });
      } else {
        await rbacService.createApplication({ app_name: appName });
        toast({ status: "success", title: "Aplikasi dibuat" });
      }
      onClose();
      await load();
    } catch (e: any) {
      toast({ status: "error", title: "Operasi gagal", description: e?.response?.data?.detail || e?.message });
    }
  };

  const remove = async (row: Application) => {
    if (!window.confirm(`Hapus aplikasi '${row.app_name}'? Ini akan menghapus semua data terkait.`)) return;
    try {
      await rbacService.deleteApplication(row.id);
      toast({ status: "success", title: "Aplikasi dihapus" });
      await load();
    } catch (e: any) {
      toast({ status: "error", title: "Gagal menghapus", description: e?.response?.data?.detail || e?.message });
    }
  };

  return (
    <Box p={4}>
      <Flex justify="space-between" align="center" mb={4}>
        <Heading size="md">Applications</Heading>
        <HStack>
          <IconButton aria-label="refresh" icon={<RepeatIcon />} onClick={() => load()} isLoading={loading} />
          <Button leftIcon={<AddIcon />} colorScheme="green" onClick={openCreate}>New</Button>
        </HStack>
      </Flex>

      <HStack mb={3}>
        <Input placeholder="Cari aplikasi..." value={search} onChange={(e) => setSearch(e.target.value)} />
      </HStack>

      <Table size="sm" variant="striped">
        <Thead>
          <Tr>
            <Th width="280px">ID</Th>
            <Th>App Name</Th>
            <Th isNumeric>Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {filtered.map((row) => (
            <Tr key={row.id}>
              <Td>{row.id}</Td>
              <Td>{row.app_name}</Td>
              <Td isNumeric>
                <HStack justify="flex-end">
                  <IconButton aria-label="edit" icon={<EditIcon />} onClick={() => openEdit(row)} size="sm" />
                  <IconButton aria-label="delete" icon={<DeleteIcon />} onClick={() => remove(row)} colorScheme="red" size="sm" />
                </HStack>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editing ? "Edit Application" : "Create Application"}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Input placeholder="App name" value={appName} onChange={(e) => setAppName(e.target.value)} />
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={onClose}>Cancel</Button>
            <Button colorScheme="green" onClick={submit} isDisabled={!appName.trim()}>Save</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ApplicationsPage;
